import React, { useEffect, useRef, useState } from "react";
import EmailPreviewFrame from "@/components/settings/EmailPreviewFrame";
import {
  getOrganizationSettingsErrorMessage,
  organizationSettingsService,
  type AutomatedEmailTemplateKey,
} from "@/services/organizationSettings";

interface AutomatedEmailPreviewProps {
  templateKey: AutomatedEmailTemplateKey;
  subject: string;
  body: string;
  /** Sizes the preview; the email scrolls inside its card. */
  className?: string;
  /** A control at the right of the subject bar (e.g. open full size). */
  action?: React.ReactNode;
}

const DEBOUNCE_MS = 500;

/**
 * Live preview of an automated email rendered by the server against a sample
 * deal. Re-renders (debounced) as the admin edits, so unsaved changes show up.
 */
const AutomatedEmailPreview: React.FC<AutomatedEmailPreviewProps> = ({
  templateKey,
  subject,
  body,
  className,
  action,
}) => {
  const [previewSubject, setPreviewSubject] = useState<string>("");
  const [html, setHtml] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await organizationSettingsService.previewAutomatedEmail({
          key: templateKey,
          subject,
          body,
        });
        if (requestId !== requestIdRef.current) return;
        setPreviewSubject(result.subject);
        setHtml(result.html);
        setError(null);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(getOrganizationSettingsErrorMessage(err));
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [templateKey, subject, body]);

  return (
    <EmailPreviewFrame
      subject={previewSubject}
      html={html}
      loading={loading}
      error={error}
      className={className}
      action={action}
      data-testid={`automated-email-${templateKey}-preview`}
    />
  );
};

export default AutomatedEmailPreview;
